import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Alert } from "../../components/Alert";
import { BillingMethodLabel, BillingStatusBadge } from "../../components/billing/BillingBadges";
import { Modal } from "../../components/crm/Modal";
import { RequirePermission } from "../../components/crm/RequirePermission";
import { Spinner } from "../../components/Spinner";
import { StepUpDialog } from "../../components/StepUpDialog";
import { ApiError, mfaChallenge } from "../../lib/api";
import {
  billingErrorCode,
  deleteInvoice,
  getInvoice,
  isApiError,
  listAccounts,
  payInvoice,
  sendInvoice,
  voidInvoice,
} from "../../lib/billing-api";
import type { Account, InvoiceDetail, PaymentMethod } from "../../lib/billing-types";
import {
  formatDate,
  formatMoney,
  formatPercent,
  formatQuantity,
  fromMajorInput,
  toMajorInput,
} from "../../lib/format";
import type { TranslationKey } from "../../lib/i18n";
import { useConfig } from "../../state/config-context";
import { useSession } from "../../state/session-context";

const ERROR_KEYS: Record<string, TranslationKey> = {
  invalid_state_transition: "billing.error.invalidTransition",
  "billing.error.invalid_state_transition": "billing.error.invalidTransition",
  "billing.error.amount_exceeds_balance": "billing.error.amountExceedsBalance",
  "billing.error.currency_mismatch": "billing.error.currencyMismatch",
  billing_item_immutable: "billing.error.itemImmutable",
  billing_invoice_not_found: "billing.error.notFound",
};

const METHODS: PaymentMethod[] = ["transfer", "cash", "check", "card", "other", "external_ref"];

function Field({ label, children }: { label: string; children: ReactNode }): ReactNode {
  return (
    <div className="detail-list__row">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function InvoiceDetailInner({ id }: { id: string }): ReactNode {
  const { t, lang } = useConfig();
  const { hasPermission } = useSession();
  const navigate = useNavigate();

  const canSend = hasPermission("billing.invoice.send");
  const canPay = hasPermission("billing.invoice.pay");
  const canVoid = hasPermission("billing.invoice.void");
  const canUpdate = hasPermission("billing.invoice.update");
  const canDelete = hasPermission("billing.invoice.delete");

  const [detail, setDetail] = useState<InvoiceDetail | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [panel, setPanel] = useState<"pay" | "void" | null>(null);
  const [pending, setPending] = useState<(() => Promise<void>) | null>(null);
  const [challengeBusy, setChallengeBusy] = useState(false);
  const [challengeError, setChallengeError] = useState<string | null>(null);

  const [payAmount, setPayAmount] = useState("");
  const [payMethod, setPayMethod] = useState<PaymentMethod>("transfer");
  const [payDate, setPayDate] = useState("");
  const [payReference, setPayReference] = useState("");
  const [payNotes, setPayNotes] = useState("");
  const [voidReason, setVoidReason] = useState("");

  const locale = lang === "es" ? "es-ES" : "en-US";

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const [invoice, accountPage] = await Promise.all([getInvoice(id), listAccounts()]);
      setDetail(invoice);
      setAccounts(accountPage.items);
    } catch (caught) {
      setError(isApiError(caught) ? caught.detail : t("error.network"));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const runAction = useCallback(
    async (action: () => Promise<void>): Promise<void> => {
      setActionError(null);
      setBusy(true);
      try {
        await action();
        setPanel(null);
        await load();
      } catch (caught) {
        if (caught instanceof ApiError && caught.code === "mfa_required") {
          setChallengeError(null);
          setPending(() => action);
          return;
        }
        const code = billingErrorCode(caught);
        const key = code === null ? undefined : ERROR_KEYS[code];
        setActionError(
          key !== undefined
            ? t(key)
            : isApiError(caught)
              ? caught.detail
              : t("error.network"),
        );
      } finally {
        setBusy(false);
      }
    },
    [load, t],
  );

  const onChallengeSubmit = async (code: string): Promise<void> => {
    setChallengeBusy(true);
    setChallengeError(null);
    try {
      await mfaChallenge(code);
      const action = pending;
      setPending(null);
      if (action !== null) {
        await runAction(action);
      }
    } catch (caught) {
      setChallengeError(caught instanceof ApiError ? caught.detail : t("error.network"));
    } finally {
      setChallengeBusy(false);
    }
  };

  if (loading) {
    return <Spinner label={t("app.loading")} />;
  }
  if (detail === null) {
    return (
      <section className="page">
        <Alert tone="error">{error ?? t("billing.error.notFound")}</Alert>
        <Link className="btn btn--ghost" to="/billing/invoices">
          {t("billing.invoices.title")}
        </Link>
      </section>
    );
  }

  const status = detail.status;
  const accountName =
    accounts.find((account) => account.id === detail.account_id)?.name ?? detail.account_id;
  const payable = status === "sent" || status === "overdue";
  const voidable = payable || status === "draft";
  const canVoidNow = voidable && detail.amount_paid_minor === "0";

  const openPay = (): void => {
    setPayAmount(toMajorInput(detail.balance_due_minor));
    setPayMethod("transfer");
    setPayDate("");
    setPayReference("");
    setPayNotes("");
    setPanel("pay");
  };

  const onSubmitPay = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    void runAction(async () => {
      await payInvoice(detail.id, {
        amount_minor: fromMajorInput(payAmount),
        method: payMethod,
        paid_at: payDate === "" ? undefined : new Date(payDate).toISOString(),
        reference: payReference.trim() === "" ? null : payReference.trim(),
        notes: payNotes.trim() === "" ? null : payNotes.trim(),
      });
    });
  };

  const onSubmitVoid = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    void runAction(async () => {
      await voidInvoice(detail.id, { void_reason: voidReason.trim() });
    });
  };

  return (
    <section className="page" aria-labelledby="billing-invoice-detail-title">
      <header className="page__header page__header--row">
        <div>
          <p className="muted">
            <Link to="/billing/invoices">{t("billing.invoices.title")}</Link>
          </p>
          <h1 id="billing-invoice-detail-title">{t("billing.invoice.detail")}</h1>
          <p className="muted">
            <BillingStatusBadge status={status} />{" "}
            {detail.number === null ? "" : `· #${String(detail.number)}`}
          </p>
        </div>
        <div className="inline-actions">
          {canUpdate && status === "draft" ? (
            <Link className="btn btn--ghost" to={`/billing/invoices/${detail.id}/edit`}>
              {t("common.edit")}
            </Link>
          ) : null}
          {canSend && status === "draft" ? (
            <button
              type="button"
              className="btn btn--primary"
              disabled={busy}
              onClick={() =>
                void runAction(async () => {
                  await sendInvoice(detail.id);
                })
              }
            >
              {t("billing.action.send")}
            </button>
          ) : null}
          {canPay && payable ? (
            <button type="button" className="btn btn--primary" disabled={busy} onClick={openPay}>
              {t("billing.action.pay")}
            </button>
          ) : null}
          {canVoid && canVoidNow ? (
            <button
              type="button"
              className="btn btn--ghost"
              disabled={busy}
              onClick={() => {
                setVoidReason("");
                setPanel("void");
              }}
            >
              {t("billing.action.void")}
            </button>
          ) : null}
          {canDelete ? (
            <button
              type="button"
              className="btn btn--danger"
              disabled={busy}
              onClick={() => setConfirmDelete(true)}
            >
              {t("billing.common.delete")}
            </button>
          ) : null}
        </div>
      </header>

      {error !== null ? <Alert tone="error">{error}</Alert> : null}
      {actionError !== null ? <Alert tone="error">{actionError}</Alert> : null}

      {panel === "pay" ? (
        <form className="form form--inline-card" onSubmit={onSubmitPay}>
          <h2 className="section-title">{t("billing.action.payTitle")}</h2>
          <div className="form-grid">
            <label className="field" htmlFor="pay-amount">
              <span>{t("billing.action.amount")}</span>
              <input
                id="pay-amount"
                inputMode="decimal"
                value={payAmount}
                required
                onChange={(event) => setPayAmount(event.target.value)}
              />
            </label>
            <label className="field" htmlFor="pay-method">
              <span>{t("billing.action.method")}</span>
              <select
                id="pay-method"
                value={payMethod}
                onChange={(event) => setPayMethod(event.target.value as PaymentMethod)}
              >
                {METHODS.map((method) => (
                  <option key={method} value={method}>
                    {t(`billing.method.${method === "external_ref" ? "externalRef" : method}` as TranslationKey)}
                  </option>
                ))}
              </select>
            </label>
            <label className="field" htmlFor="pay-date">
              <span>{t("billing.action.paidAt")}</span>
              <input
                id="pay-date"
                type="datetime-local"
                value={payDate}
                onChange={(event) => setPayDate(event.target.value)}
              />
            </label>
            <label className="field" htmlFor="pay-reference">
              <span>{t("billing.action.reference")}</span>
              <input
                id="pay-reference"
                value={payReference}
                maxLength={200}
                onChange={(event) => setPayReference(event.target.value)}
              />
            </label>
            <label className="field" htmlFor="pay-notes">
              <span>{t("billing.action.notes")}</span>
              <textarea
                id="pay-notes"
                value={payNotes}
                rows={2}
                maxLength={2000}
                onChange={(event) => setPayNotes(event.target.value)}
              />
            </label>
          </div>
          <div className="form-actions">
            <button type="submit" className="btn btn--primary" disabled={busy}>
              {t("billing.action.pay")}
            </button>
            <button type="button" className="btn btn--ghost" onClick={() => setPanel(null)}>
              {t("common.cancel")}
            </button>
          </div>
        </form>
      ) : null}

      {panel === "void" ? (
        <form className="form form--inline-card" onSubmit={onSubmitVoid}>
          <h2 className="section-title">{t("billing.action.voidTitle")}</h2>
          <label className="field" htmlFor="void-reason">
            <span>{t("billing.action.voidReason")}</span>
            <input
              id="void-reason"
              value={voidReason}
              maxLength={500}
              required
              aria-describedby="void-reason-hint"
              onChange={(event) => setVoidReason(event.target.value)}
            />
            <small id="void-reason-hint" className="field__hint">
              {t("billing.action.voidReasonHint")}
            </small>
          </label>
          <div className="form-actions">
            <button
              type="submit"
              className="btn btn--primary"
              disabled={busy || voidReason.trim() === ""}
            >
              {t("billing.action.void")}
            </button>
            <button type="button" className="btn btn--ghost" onClick={() => setPanel(null)}>
              {t("common.cancel")}
            </button>
          </div>
        </form>
      ) : null}

      <dl className="detail-list">
        <Field label={t("billing.invoice.account")}>{accountName}</Field>
        <Field label={t("billing.invoice.issueDate")}>
          {formatDate(detail.issue_date, locale)}
        </Field>
        <Field label={t("billing.invoice.dueDate")}>{formatDate(detail.due_date, locale)}</Field>
        <Field label={t("billing.common.currency")}>{detail.currency}</Field>
        <Field label={t("billing.invoice.created")}>
          {formatDate(detail.created_at, locale, true)}
        </Field>
        <Field label={t("billing.invoice.sentAt")}>{formatDate(detail.sent_at, locale, true)}</Field>
        <Field label={t("billing.invoice.paidAt")}>{formatDate(detail.paid_at, locale, true)}</Field>
        <Field label={t("billing.invoice.voidedAt")}>
          {formatDate(detail.voided_at, locale, true)}
        </Field>
        {detail.void_reason === null ? null : (
          <Field label={t("billing.action.voidReason")}>{detail.void_reason}</Field>
        )}
      </dl>

      <h2 className="section-title">{t("billing.invoice.items")}</h2>
      {detail.items.length === 0 ? (
        <p className="empty">{t("billing.invoice.noItems")}</p>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <caption className="sr-only">{t("billing.invoice.items")}</caption>
            <thead>
              <tr>
                <th scope="col">{t("billing.item.description")}</th>
                <th scope="col">{t("billing.item.quantity")}</th>
                <th scope="col">{t("billing.item.unitPrice")}</th>
                <th scope="col">{t("billing.item.discount")}</th>
                <th scope="col">{t("billing.item.tax")}</th>
                <th scope="col">{t("billing.common.lineTotal")}</th>
              </tr>
            </thead>
            <tbody>
              {detail.items.map((item) => (
                <tr key={item.id}>
                  <td>{item.description}</td>
                  <td>{formatQuantity(item.quantity_milli, locale)}</td>
                  <td>{formatMoney(item.unit_price_minor, detail.currency, locale)}</td>
                  <td>{formatPercent(item.discount_bp, locale)}</td>
                  <td>{item.tax_name ?? t("billing.item.taxNone")}</td>
                  <td>{formatMoney(item.line_total_minor, detail.currency, locale)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <dl className="detail-list">
        <Field label={t("billing.invoice.subtotal")}>
          {formatMoney(detail.subtotal_minor, detail.currency, locale)}
        </Field>
        <Field label={t("billing.invoice.discount")}>
          {formatMoney(detail.discount_minor, detail.currency, locale)}
        </Field>
        <Field label={t("billing.invoice.tax")}>
          {formatMoney(detail.tax_minor, detail.currency, locale)}
        </Field>
        <Field label={t("billing.invoice.total")}>
          {formatMoney(detail.total_minor, detail.currency, locale)}
        </Field>
        <Field label={t("billing.invoice.amountPaid")}>
          {formatMoney(detail.amount_paid_minor, detail.currency, locale)}
        </Field>
        <Field label={t("billing.invoice.balanceDue")}>
          {formatMoney(detail.balance_due_minor, detail.currency, locale)}
        </Field>
      </dl>

      <h2 className="section-title">{t("billing.invoice.payments")}</h2>
      {detail.payments.length === 0 ? (
        <p className="empty">{t("billing.invoice.noPayments")}</p>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <caption className="sr-only">{t("billing.invoice.payments")}</caption>
            <thead>
              <tr>
                <th scope="col">{t("billing.action.amount")}</th>
                <th scope="col">{t("billing.action.method")}</th>
                <th scope="col">{t("billing.action.paidAt")}</th>
                <th scope="col">{t("billing.action.reference")}</th>
                <th scope="col">{t("billing.common.status")}</th>
              </tr>
            </thead>
            <tbody>
              {detail.payments.map((payment) => (
                <tr key={payment.id}>
                  <td>{formatMoney(payment.amount_minor, payment.currency, locale)}</td>
                  <td>
                    <BillingMethodLabel method={payment.method} />
                  </td>
                  <td>{formatDate(payment.paid_at, locale, true)}</td>
                  <td>{payment.reference ?? t("billing.common.none")}</td>
                  <td>
                    <BillingStatusBadge status={payment.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {confirmDelete ? (
        <Modal
          title={t("billing.action.deleteTitle")}
          onClose={() => setConfirmDelete(false)}
          busy={busy}
          footer={
            <>
              <button
                type="button"
                className="btn btn--danger"
                disabled={busy}
                onClick={() =>
                  void runAction(async () => {
                    await deleteInvoice(detail.id);
                    navigate("/billing/invoices");
                  })
                }
              >
                {t("billing.common.delete")}
              </button>
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => setConfirmDelete(false)}
              >
                {t("common.cancel")}
              </button>
            </>
          }
        >
          <p>{t("billing.action.deleteTitle")}</p>
        </Modal>
      ) : null}

      <StepUpDialog
        open={pending !== null}
        busy={challengeBusy}
        error={challengeError}
        onCancel={() => {
          setPending(null);
          setChallengeError(null);
        }}
        onSubmit={(code) => void onChallengeSubmit(code)}
      />
    </section>
  );
}

function InvoiceDetailRoute(): ReactNode {
  const { id } = useParams<{ id: string }>();
  const { t } = useConfig();
  if (id === undefined) {
    return (
      <section className="page">
        <Alert tone="error">{t("billing.error.notFound")}</Alert>
        <Link className="btn btn--ghost" to="/billing/invoices">
          {t("billing.invoices.title")}
        </Link>
      </section>
    );
  }
  return <InvoiceDetailInner id={id} />;
}

export function InvoiceDetailPage(): ReactNode {
  return (
    <RequirePermission permission="billing.invoice.read">
      <InvoiceDetailRoute />
    </RequirePermission>
  );
}
